import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../../../auth/AuthContext";
import { baixaService } from "../services/baixa-service";

export const baixaResolutionKeys = {
  all: ["baixa-resolution"] as const,
  list: (dbName: string, ops: string) =>
    [...baixaResolutionKeys.all, "list", dbName, ops] as const,
};

interface UseBaixaResolutionParams {
  fundName: string;
  ops: string;
  enabled?: boolean;
}

export function useBaixaResolution({
  fundName,
  ops,
  enabled = true,
}: UseBaixaResolutionParams) {
  const { user } = useAuth();

  const {
    data: baixaList = [],
    isPending,
    refetch,
  } = useQuery({
    queryKey: baixaResolutionKeys.list(fundName, ops),
    queryFn: async () => {
      const response = await baixaService.getBaixaList({
        dbName: fundName,
        ops,
      });
      return response.data || [];
    },
    enabled: enabled && !!fundName && !!ops,
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 20,
  });

  const startDocMutation = useMutation({
    mutationFn: async ({
      filterDate,
      ops: mutationOps,
    }: {
      filterDate: string;
      ops: string;
    }) => {
      if (!fundName || !user?.email) throw new Error("Missing required data");
      return baixaService.startBaixaDoc({
        dbName: fundName,
        filterDate,
        ops: mutationOps,
        userInfo: { email: user.email },
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetch();
      }, 3000);
    },
  });

  const submitDbMutation = useMutation({
    mutationFn: async ({
      filterDate,
      ops: mutationOps,
    }: {
      filterDate: string;
      ops: string;
    }) => {
      if (!fundName || !user?.email) throw new Error("Missing required data");
      return baixaService.submitBaixaDb({
        dbName: fundName,
        filterDate,
        ops: mutationOps,
        userInfo: { email: user.email },
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetch();
      }, 3000);
    },
  });

  const startDoc = (filterDate: string, ops: string) => {
    startDocMutation.mutate({ filterDate, ops });
  };

  const submitDb = (filterDate: string, ops: string) => {
    submitDbMutation.mutate({ filterDate, ops });
  };

  return {
    baixaList,
    isLoading: isPending,
    startDoc,
    submitDb,
    isStartingDoc: startDocMutation.isPending,
    isSubmittingDb: submitDbMutation.isPending,
    refetch,
  };
}
